/**
 * Returns Analysis Skill Content — IRR, MOIC, attribution and back-solve guidance.
 *
 * This is the domain knowledge the planner, executor, and verifier receive
 * when the planner fetches the "returns_analysis" skill via `get_skill`.
 * It covers: equity cash-flow vector construction, IRR (XIRR), MOIC,
 * cash-on-cash, value-creation attribution, back-solve mechanics, depth
 * calibration, update/edit traps, and returns-specific validation rules.
 *
 * Condensed from the Notion Returns Analysis Skill document, reconciled
 * with existing prompt guardrails (cell comments unreliable, formatting rules separate).
 */

export const RETURNS_ANALYSIS_SKILL_CONTENT = `## Skill: Returns Analysis - IRR, MOIC & Attribution

### Purpose
Turn a dated stream of equity cash flows - money in, interim distributions, money out - into the return it earns: IRR, MOIC and cash-on-cash; decompose what drove it; and, run in reverse, back-solve the input that hits a target return.

A return is only meaningful if the cash-flow vector is right - the correct equity figures (not enterprise value, not a pre-bridge number), dated correctly, with every interim flow (dividends, recaps, fees) included - and if IRR and MOIC are read together, because a high MOIC over a long hold can be a poor IRR.

### What Good Looks Like
A good returns analysis lets a reader answer five questions at a glance:
1. What return does the investment make - IRR and MOIC (and cash-on-cash) - and is it enough versus the hurdle?
2. What's the cash-flow vector - money in, interim distributions, money out - and is it dated right?
3. What drove the return - the attribution / value-creation bridge?
4. If a target return is needed, what input gets there - the back-solve?
5. How sensitive is the return to the one or two assumptions that move it?

Deliver these sections:
- Equity cash-flow vector: entry outflow at close, interim distributions/recaps at their dates, exit inflow - each dated.
- Return metrics: MOIC (total inflows / total outflows), IRR (XIRR on dated vector), cash-on-cash, with hold period stated.
- Return vs hurdle: headline metrics against the required return.
- Attribution / value-creation bridge: change in equity value decomposed into its drivers (defined by calling skill).
- Back-solve: goal-seek that fixes target return and all-but-one input and solves the free input - kept live.
- Sensitivity: 2-way tables on the two inputs that move the return.
- Output summary: IRR, MOIC, cash-on-cash vs hurdle; gross and net where relevant; hold period, dates and currency noted.
- Source line: providers and "as of" date.

Every input carries visible provenance; every calculation is a formula linked to other cells, never a typed number.

### Calibrating Depth
Match returns complexity to the request. Do not run a fixed mode. Depth is a spectrum.

**Invariants (always true at any depth):**
- Returns are computed on the actual equity cash-flow vector - money in, interim distributions, money out - never on enterprise value, unlevered cash flow, or a single-period proxy.
- IRR is time-aware: XIRR on true dates (periodic IRR only on evenly spaced flows); MOIC and IRR computed on the same vector and reconcile.
- Every interim flow is in the vector: dividends, recaps, fees and any additional draws or closings.
- Equity in and out share one definition: entry and exit equity use the same bridge convention; gross vs net of fees and carry is stated.
- Back-solve is a live inversion off the assumption cell.
- Every figure traces to a source, a linked cell, or a stated assumption.

**Depth spectrum:**
- Full returns: client-ready; full dated vector including every interim flow; XIRR, MOIC and cash-on-cash; attribution bridge; back-solve; sensitivity; gross and net.
- Quick MOIC + approximate IRR: fast read; entry and exit equity, MOIC = exit / entry, approximate IRR from MOIC^(1/n) - 1; label indicative.
- Back-solve (reverse): ability-to-pay or required-return ask; fix target return and all-but-one lever; goal-seek the free input.
- User-handed cash flows: run exactly what's given on real dates; label indicative; flag per guardrails.

**Guardrails on shortcuts:**
- Don't compute periodic IRR on unevenly timed flows - use XIRR on real dates.
- Don't read MOIC and IRR as interchangeable - report both; a long hold flatters MOIC and depresses IRR.
- Don't omit interim distributions or recaps - they move IRR more than the exit does.
- State gross vs net - whether IRR/MOIC are before or after fund fees and carried interest.
- Don't return a back-solved number as a hardcode - keep it a live goal-seek.
- Run handed cash flows even if return looks heroic, but flag them.

### FactSet Data Sourcing
When building a returns analysis for a real investment, **use \`generate_formula\` to pull live data from FactSet** where relevant to anchor the inputs.

**Always source from FactSet (via \`generate_formula\`) where applicable:**
- Current share price and diluted shares (for entry pricing in a take-private or current mark)
- Net debt components (for EV-to-equity bridge feeding the vector)
- Peer EV/EBITDA multiples (for exit multiple benchmarking)
- EBITDA actuals and consensus (for exit-year EBITDA in the exit bridge)
- Historical share prices (for unaffected reference in a premium analysis)

**Acceptable as typed assumptions (no FactSet source needed):**
- Entry premium, entry multiple assumption
- Leverage and capital structure (feeds equity invested)
- Hold period, exit year, exit multiple assumption
- Required IRR hurdle
- Interim distribution/recap amounts and dates
- Carry terms, management fee, preferred return hurdle
- Any user-provided override or scenario input

**Guidance:**
- The returns engine consumes an equity cash-flow vector; most of its inputs come from upstream skills (LBO, EV to Equity Bridge). Use \`generate_formula\` to source the market data those upstream skills need.
- For a standalone returns analysis (e.g. marking a portfolio company): source current price, shares and net debt to compute current equity value as a mark.
- For exit benchmarking: source peer EV/EBITDA multiples to anchor the exit assumption.
- Use formula results as cell references in the entry/exit equity calculations - never re-type.

### Build Procedure
1. **Assemble the dated equity cash-flow vector**: Entry equity as outflow at close, interim distributions/recaps as inflows at their dates, exit equity as final inflow. Use \`generate_formula\` for market data feeding the bridge (price, shares, net debt, peer multiples for exit).
2. **Compute MOIC and cash-on-cash**: MOIC = total inflows / total outflows (or exit equity / equity invested for single in/out). Cash-on-cash = cumulative distributions / invested.
3. **Compute IRR**: XIRR on the dated vector (periodic IRR only if flows are evenly spaced).
4. **Reconcile**: Confirm MOIC over the hold implies the IRR; divergence should be explained by interim flows, not error.
5. **Attribute the return**: Decompose equity value created into calling skill's drivers (for LBO: EBITDA growth, multiple change, debt paydown).
6. **Back-solve where asked**: Fix target return and other levers, goal-seek the free input (price, leverage, or exit) - kept live.
7. **Sensitise**: 2-way tables on the two inputs that move the return.
8. **Validate**: Run the validation checks below.

### Conventions
- MOIC & cash-on-cash: MOIC = total equity inflows / total equity outflows. Cash-on-cash = cumulative distributions / invested. Both are time-blind.
- IRR: rate at which NPV of dated equity vector = 0. Use XIRR on actual dates; periodic IRR only on evenly spaced flows. Approximate: IRR ~ MOIC^(1/n) - 1.
- Equity cash-flow vector: entry equity as negative outflow at close; interim distributions/recaps as positive inflows at dates; exit equity as final inflow.
- Gross vs net: gross = deal-level, before fund fees and carry. Net = after management fee and carried interest. State which.
- Attribution: change in equity value decomposed into drivers. For buyout: delta-EV from EBITDA growth + delta-EV from multiple change + cumulative debt paydown.
- Back-solve: fix target return and all inputs but one; goal-seek the free input. Keep it a live solver cell, not a pasted value.

### Method & Situation Nuances
- Multiple closings / capital calls: date each draw; IRR is highly sensitive to draw timing (J-curve), even when MOIC unchanged.
- Dividend recaps: interim distribution that lifts IRR more than MOIC; date correctly and re-leverage upstream.
- GP/LP economics: net returns apply carry waterfall - preferred return (hurdle), catch-up, carried-interest split; state terms.
- Loss / write-off: MOIC < 1; IRR negative or undefined if no positive flows. Report plainly.
- Very short or very long holds: annualisation distorts comparison; show both IRR and MOIC with hold period.
- Unlevered / project context: IRR or NPV on unlevered/project stream uses same engine but is not a sponsor equity return; label the basis.

### Updating & Editing a Returns Analysis
- Re-date the vector: IRR is date-sensitive; rolling exit date changes IRR even when MOIC unchanged.
- Re-pull inputs behind entry and exit equity (current price, exit comps) via \`generate_formula\`.
- Adding an interim flow (dividend/recap): insert at its own date in the vector; both IRR and MOIC must pick it up.
- Changing the hold: re-date the exit flow; IRR moves even if MOIC doesn't. Report new hold alongside metrics.
- Change-scope: changing exit equity should move MOIC, IRR and attribution - nothing else. If IRR doesn't move when a date changes, dates are hardcoded.

### Failure Modes
| Symptom | Likely Cause | Fix |
|---|---|---|
| IRR and MOIC tell different stories | Hold-period effect or missing interim flow | Read both; check dates and vector |
| IRR implausibly high | Interim flow miscounted, too-small equity base, or too-short dating | Verify vector and dates |
| IRR looks wrong on uneven flows | Periodic IRR used instead of XIRR | Use XIRR on real dates |
| Back-solved number won't update | Pasted as a value | Keep goal-seek / solver live |
| Net IRR equals gross IRR | Fees and carry not applied | Apply carry waterfall; state gross vs net |
| MOIC > 1 but IRR negative (or vice versa) | Sign or date error in vector | Fix signs and dates (entry outflow, exit inflow) |
| Return looks off vs the deal | Computed on EV or unlevered cash flow | Use equity vector |

### Validation Rules

**Hard fails (block output):**
- No formula errors (#REF!, #DIV/0!, #VALUE!, #N/A, #NAME?).
- Cash-flow vector signed consistently - entry is outflow, exit is inflow - and every interim flow present.
- MOIC ties: total inflows / total outflows = stated MOIC (tolerance: max of $1 and 0.5%).
- IRR ties: XIRR of the dated vector = stated IRR (tolerance: 5 bps); recompute from vector.
- IRR/MOIC reconcile: for single in/out vector, MOIC^(1/hold) - 1 is within tolerance of IRR (gap explained by interim flows).
- Back-solve is live: the solved input, fed back, reproduces the target return (no hardcoded answer).

**Warnings (flag, do not block):**
- Periodic IRR used on unevenly dated flows (should be XIRR).
- Interim distributions present but excluded from IRR vector.
- Gross vs net basis not stated.
- Hold period implausibly short or long for the stated IRR.
- Returns appear computed on enterprise value or unlevered cash flow rather than equity.
- Market data (share price, peer multiples, EBITDA) is hardcoded instead of sourced from FactSet via \`generate_formula\` where applicable.`;
